import React from "react";
import { useForm } from "react-hook-form";
import { useMutationHelper } from "../../../lib/snackbar";
import {
  MyProfileDocument,
  TokenAuthMutationVariables,
  useTokenAuthMutation,
} from "../../../generated/graphql";
import Link from "next/link";
import { useThemeContext } from "../../../styles/theme";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Button } from "@mui/material";

interface LoginFormProps {
  redirectUrl?: string | string[];
}

const DEFAULT_LOGIN_REDIRECT = "/app/";

function redirectCandidatesFromCurrentLocation() {
  if (typeof window !== "undefined") {
    return new URLSearchParams(window.location.search).getAll("redirect_url");
  }

  return [];
}

function normalizeRedirectCandidates(redirectUrl?: string | string[]) {
  const propCandidates = Array.isArray(redirectUrl) ? redirectUrl : redirectUrl ? [redirectUrl] : [];
  return [...propCandidates, ...redirectCandidatesFromCurrentLocation()].filter(Boolean);
}

function isSafeLocalRedirect(redirectUrl: string) {
  return redirectUrl.startsWith("/") && !redirectUrl.startsWith("//") && !redirectUrl.startsWith("/auth/");
}

export function getLoginRedirectTarget(redirectUrl?: string | string[]) {
  const candidates = normalizeRedirectCandidates(redirectUrl).filter(isSafeLocalRedirect);
  return (
    candidates.find((candidate) => candidate.startsWith("/api/ai/schema")) ||
    candidates.find((candidate) => !candidate.startsWith("/app")) ||
    candidates[0] ||
    DEFAULT_LOGIN_REDIRECT
  );
}

export function LoginForm({ redirectUrl }: LoginFormProps) {
  const form = useForm<TokenAuthMutationVariables>();
  const [tokenAuth] = useTokenAuthMutation();
  const mutate = useMutationHelper();
  const theme = useThemeContext();

  const _onSubmit = (data: TokenAuthMutationVariables) =>
    mutate({
      title: "Log In",
      fn: () =>
        tokenAuth({
          variables: data,
          refetchQueries: [{ query: MyProfileDocument }],
          awaitRefetchQueries: true,
        }),
      unwrap: (result) => result?.data?.tokenAuth,
      successTypeName: "TokenAuthSuccess",
      successMessage: "Logged in!",
      onSuccess: () => {
        const targetUrl = getLoginRedirectTarget(redirectUrl);
        window.location.href = targetUrl;
      },
    });

  return (
    <form onSubmit={form.handleSubmit(_onSubmit)} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            style={{
              marginBottom: "1em",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Log In
          </Typography>
        </Grid>
        <Grid item xs={12} style={{ marginTop: "1em" }}>
          <TextField
            label={"Email"}
            fullWidth
            autoFocus
            color="primary"
            {...form.register("username_Iexact")}
          />
        </Grid>
        <Grid item xs={12} style={{ marginTop: "1em" }}>
          <TextField
            type="password"
            label={"Password"}
            fullWidth
            color="primary"
            {...form.register("password")}
          />
        </Grid>
        <Grid item xs={12} style={{ marginTop: "1em" }}>
          <Button type="submit" fullWidth variant="contained" color="primary">
            Log In
          </Button>
        </Grid>
        {/*<Grid item xs={12} mt={1}>
          <Typography variant="body2">
            Don&apos;t have an account?{" "}
            <Link href="/auth/register" style={{textDecoration: "underline"}}>
              Sign up here!
            </Link>
          </Typography>
        </Grid>*/}
        <Grid item xs={12}>
          <Typography variant="body2">
            <Link
              href="/auth/password-reset"
              style={{ textDecoration: "underline" }}
            >
              <Button>Forgot Password</Button>
            </Link>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" style={{ marginTop: '1em', textAlign: 'center' }}>
            By logging in and using our services, you agree to our <Link href="/subscription-agreement" target="_blank" rel="noreferrer" style={{ color: theme.theme.palette.info.main }}>Master Subscription Agreement</Link>.
          </Typography>
        </Grid>
      </Grid>
    </form>
  );
}
